import React, { useEffect, useState } from "react";

import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import ImportCsvWizard from "@/components/leads/ImportCsvWizard";

const formatErrorDetail = (detail) => {
  if (!detail) return "Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join(" · ");
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
};

const normalizeDateInput = (value) => {
  if (!value) return null;
  if (value.includes("/")) {
    const parts = value.split("/");
    if (parts.length === 3) {
      const [mm, dd, yyyy] = parts;
      if (mm && dd && yyyy) {
        return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
      }
    }
  }
  return value;
};

export default function AddLeadDialog({ open, onOpenChange, mode = "single", onCreated }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    lead_source: "",
    ad_set_name: "",
    ad_name: "",
    lead_external_id: "",
    campaign_name: "",
    form_name: "",
    full_name: "",
    phone_number: "",
    email: "",
    state: "",
    county: "",
    dob: "",
    lead_type: "",
    referral_agent_name: "",
    lead_create_date: "",
  });

  useEffect(() => {
    if (!open) {
      setSaving(false);
      setForm({
        lead_source: "",
        ad_set_name: "",
        ad_name: "",
        lead_external_id: "",
        campaign_name: "",
        form_name: "",
        full_name: "",
        phone_number: "",
        email: "",
        state: "",
        county: "",
        dob: "",
        lead_type: "",
        referral_agent_name: "",
        lead_create_date: "",
      });
    }
  }, [open]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async () => {
    if (!form.full_name.trim()) {
      toast({ title: "Missing name", description: "Full Name is required.", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
    const payload = {
        ...form,
        full_name: form.full_name.trim(),
        lead_source: form.lead_source.trim() || null,
        ad_set_name: form.ad_set_name.trim() || null,
        ad_name: form.ad_name.trim() || null,
        lead_external_id: form.lead_external_id.trim() || null,
        campaign_name: form.campaign_name.trim() || null,
        form_name: form.form_name.trim() || null,
        phone_number: form.phone_number.trim() || null,
        email: form.email.trim() || null,
        state: form.state.trim() || null,
        county: form.county.trim() || null,
      dob: normalizeDateInput(form.dob),
        lead_type: form.lead_type.trim() || null,
        referral_agent_name: form.referral_agent_name.trim() || null,
      lead_create_date: normalizeDateInput(form.lead_create_date),
      };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "") payload[key] = null;
    });

      const res = await api.post("/leads", payload);
      toast({ title: "Lead created" });
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast({
        title: "Could not create lead",
        description: formatErrorDetail(e?.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-5xl" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "csv" ? "Upload CSV" : "Add Lead"}</DialogTitle>
        </DialogHeader>

        {mode === "csv" ? (
          <ImportCsvWizard
            onBack={() => onOpenChange(false)}
            onCancel={() => onOpenChange(false)}
            onImported={() => {
              onOpenChange(false);
              onCreated?.();
            }}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-slate-200/80">Lead Source</Label>
                <Input value={form.lead_source} onChange={onChange("lead_source")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-source" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200/80">Lead Create Date</Label>
                <Input type="date" value={form.lead_create_date} onChange={onChange("lead_create_date")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-create-date" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200/80">Ad Set Name</Label>
                <Input value={form.ad_set_name} onChange={onChange("ad_set_name")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-ad-set-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200/80">Ad Name</Label>
                <Input value={form.ad_name} onChange={onChange("ad_name")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-ad-name" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200/80">Campaign Name</Label>
                <Input value={form.campaign_name} onChange={onChange("campaign_name")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-campaign-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200/80">Form Name</Label>
                <Input value={form.form_name} onChange={onChange("form_name")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-form-name" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200/80">Lead ID</Label>
                <Input value={form.lead_external_id} onChange={onChange("lead_external_id")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-external-id" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200/80">Full Name *</Label>
                <Input value={form.full_name} onChange={onChange("full_name")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-full-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200/80">Phone Number</Label>
                <Input value={form.phone_number} onChange={onChange("phone_number")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-phone" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200/80">Email</Label>
                <Input value={form.email} onChange={onChange("email")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-email" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200/80">State</Label>
                <Input value={form.state} onChange={onChange("state")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-state" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200/80">County</Label>
                <Input value={form.county} onChange={onChange("county")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-county" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200/80">DOB</Label>
                <Input type="date" value={form.dob} onChange={onChange("dob")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-dob" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200/80">Lead Type</Label>
                <Input value={form.lead_type} onChange={onChange("lead_type")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-type" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200/80">Referral Agent Name</Label>
                <Input value={form.referral_agent_name} onChange={onChange("referral_agent_name")} className="bg-white/5 ring-1 ring-white/10" data-testid="add-lead-referral-agent" />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => onOpenChange(false)}
                data-testid="add-lead-cancel"
              >
                Cancel
              </Button>
              <Button onClick={onSubmit} disabled={saving} data-testid="add-lead-submit">
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
